import { useEffect, useMemo, useState } from 'react'

import Card from 'apps/employer_renewals/components/card/Card'
import PageContainer from 'apps/employer_renewals/components/layout/PageContainer'
import NavigationButtons from 'apps/employer_renewals/components/NavigationButtons'
import useAppNavigation from 'apps/employer_renewals/hooks/useAppNavigation'
import useQuoteSummary from 'apps/employer_renewals/hooks/useQuoteSummary'
import { useAllowances } from 'apps/employer_renewals/providers/AllowancesProvider'
import { useEmployerRenewalConfig } from 'apps/employer_renewals/providers/EmployerRenewalConfigProvider'
import { EmployerRenewalsRoute } from 'apps/employer_renewals/routes'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Slider } from 'components/ui/slider'
import { Banner } from 'slab'
import { formatMoney } from 'utils'

const DependentCoverageSlider = (
  { label, currentYearPercentage, value, onChange }:
  { label: string; currentYearPercentage: number; value: number; onChange: (percentage: number) => void }
) => {
  return (
    <div className='grid grid-cols-6 gap-2 w-full items-center'>
      <div className='flex flex-col col-span-2'>
        <span className='body-sm text-main'>{label}</span>
        <span className='caption text-subdued'>Current: {currentYearPercentage}%</span>
      </div>
      <div className='flex items-center flex-1 col-span-4 pr-6'>
        <span className='body-sm-bold text-main mr-4'>{value}%</span>
        <Slider
          size='large'
          trackColor='bg-[#CFCFCF]'
          min={0}
          max={100}
          value={[value]}
          onValueChange={v => onChange(v[0])}
          data-testid='employer-employee-contribution-slider'
        />
      </div>
    </div>
  )
}

export default function DependentsCoveragePage () {
  const { navigateTo, navigateBack } = useAppNavigation()
  const { isUsingFlatAllowances, employerRenewalConfig, isLoading: isLoadingRenewalConfig } = useEmployerRenewalConfig()
  const {
    renewalYearAllowances,
    setRenewalYearAllowances,
    renewalApproach,
    saveAllowances,
    isSavingAllowances,
  } = useAllowances()

  const {
    renewalYearCost,
    numEmployeesWithDependents,
    numDependentsAcrossAllEmployees,
    calculateRenewalYearAdditionalDependentCoverageCosts,
    isLoading,
  } = useQuoteSummary({ renewalApproach, period: 'annual' })

  const [persistedSpousePercentage, setPersistedSpousePercentage] = useState<number | null>(null)
  const [persistedAdditionalDependentPercentage, setPersistedAdditionalDependentPercentage] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const dynamicAllowance = renewalYearAllowances[0]
  const configuredSpousePercentage = dynamicAllowance?.spouse_contribution_percentage || 0
  const configuredAdditionalDependentPercentage = dynamicAllowance?.dependent_contribution_percentage || 0

  // Keep track of what values are persisted in the DB by storing them once the renewal config is loaded
  useEffect(() => {
    if (!isLoadingRenewalConfig && persistedSpousePercentage === null && persistedAdditionalDependentPercentage === null) {
      setPersistedSpousePercentage(configuredSpousePercentage)
      setPersistedAdditionalDependentPercentage(configuredAdditionalDependentPercentage)
    }
  }, [isLoadingRenewalConfig])

  const saveAllowancesAndContinue = async () => {
    setErrorMessage(null)
    const isSaved = await saveAllowances()
    if (!isSaved) {
      setErrorMessage('Failed to save allowances, please try again or contact support if the problem persists.')
      return
    }

    setPersistedSpousePercentage(configuredSpousePercentage)
    setPersistedAdditionalDependentPercentage(configuredAdditionalDependentPercentage)
    navigateTo(EmployerRenewalsRoute.ALLOWANCES_CONFIRM)
  }

  const revertUnsavedChangesAndNavigateBack = () => {
    if (persistedSpousePercentage !== null && persistedAdditionalDependentPercentage !== null) {
      setRenewalYearAllowances({
        ...dynamicAllowance,
        spouse_contribution_percentage: persistedSpousePercentage,
        dependent_contribution_percentage: persistedAdditionalDependentPercentage
      })
    }
    navigateBack()
  }

  const additionalDependentCoverageCost = useMemo(() => (
    calculateRenewalYearAdditionalDependentCoverageCosts(configuredSpousePercentage, configuredAdditionalDependentPercentage)
  ), [configuredSpousePercentage, configuredAdditionalDependentPercentage, calculateRenewalYearAdditionalDependentCoverageCosts])

  if (isUsingFlatAllowances || renewalYearAllowances.length > 1) {
    navigateTo(EmployerRenewalsRoute.ALLOWANCES_CONFIRM)
    return
  }

  return (
    <PageContainer maxWidth='md'>
      <Card
        className='border-emphasis shadow-emphasis'
        style={{ background: 'linear-gradient(180deg, var(--color-primitive-thatch-200, #F1E2FF) -10.45%, var(--color-primitive-thatch-100, #F9F4FF) 12.98%, rgba(249, 244, 255, 0.00) 100%)' }}
        footer={
          <div className='grid grid-cols-12'>
            <div className='col-span-8'>
              <h3 className='body-bold text-main'>Dependent coverage</h3>
              <div className='flex flex-col gap-2 mt-4'>
                <DependentCoverageSlider
                  label='First dependent'
                  currentYearPercentage={persistedSpousePercentage || 0}
                  value={configuredSpousePercentage}
                  onChange={v => setRenewalYearAllowances({ ...dynamicAllowance, spouse_contribution_percentage: v })}
                />
                <DependentCoverageSlider
                  label='Additional dependents'
                  currentYearPercentage={persistedAdditionalDependentPercentage || 0}
                  value={configuredAdditionalDependentPercentage}
                  onChange={v => setRenewalYearAllowances({ ...dynamicAllowance, dependent_contribution_percentage: v })}
                />
              </div>
            </div>
            <div className='flex flex-col gap-2 col-span-4'>
              <div>
                <h3 className='body-bold text-main'>Estimated {employerRenewalConfig?.renewal_year} dependent cost</h3>
                <p className='caption text-subdued'>Based on {numDependentsAcrossAllEmployees} dependents from {numEmployeesWithDependents} employee{numEmployeesWithDependents > 1 ? 's' : ''}</p>
              </div>
              <div>
                {isLoading
                  ? <SkeletonItem widthClass='w-24' />
                  : (
                    <>
                      <h2 className='heading-lg text-black'>
                        {additionalDependentCoverageCost > 0 ? '+' : ''} {formatMoney(additionalDependentCoverageCost)}
                      </h2>
                      <p className='caption text-subdued'>Current {employerRenewalConfig?.renewal_year} estimate: {formatMoney(renewalYearCost)}</p>
                    </>
                    )}
              </div>
            </div>
          </div>
        }
      >
        <div className='flex flex-col gap-6'>

          <div className='flex items-center gap-3'>
            <div className='flex shadow-sm border border-violet-300 bg-violet-50 py-1 px-2 rounded-md text-violet-500 body-sm-bold'>Recommended</div>
            <h2 className='heading-sm text-main'>Add more dependent coverage</h2>
          </div>

          <p className='body-sm text-main'>
            Providing dependent coverage helps keep your employees satisfied by extending health benefits to their loved ones.
          </p>
        </div>
      </Card>
      {errorMessage && (
        <Banner type='critical' title='An error occurred'>
          {errorMessage}
        </Banner>
      )}
      <NavigationButtons
        nextButtonText={configuredAdditionalDependentPercentage === 0 && configuredSpousePercentage === 0 ? 'Continue without dependent coverage' : 'Update dependent coverage'}
        onNext={saveAllowancesAndContinue}
        onBack={revertUnsavedChangesAndNavigateBack}
        isLoading={isSavingAllowances}
      />
    </PageContainer>
  )
}
